import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { ServerTokenService } from '../../services/server-token.service';
import {
  CensusActionLabel,
  CensusCodesByActionType,
} from './census-action-labels.models';

@Injectable({ providedIn: 'root' })
export class CensusActionLabelsService {
  private readonly base = 'snfs/census-action-labels';

  constructor(private server: ServerTokenService) {}

  listLabels(): Observable<CensusActionLabel[]> {
    return this.server.getWithToken<CensusActionLabel[]>(this.base);
  }

  listCodes(): Observable<CensusCodesByActionType> {
    return this.server.getWithToken<CensusCodesByActionType>(`${this.base}/codes`);
  }

  createLabel(payload: { actionTypes: string[]; name: string; displayOrder?: number }): Observable<CensusActionLabel> {
    return this.server.postWithToken<CensusActionLabel>(this.base, payload);
  }

  updateLabel(
    id: string,
    payload: { name?: string; displayOrder?: number },
  ): Observable<CensusActionLabel> {
    return this.server.patchWithToken<CensusActionLabel>(`${this.base}/${id}`, payload);
  }

  deleteLabel(id: string): Observable<void> {
    return this.server.deleteWithToken<void>(`${this.base}/${id}`);
  }

  // shortDesc lives in the body since real PCC short descs can contain '/' or '.'.
  // labelIds=[] clears the assignment — the code's events stop appearing in the report.
  setLabels(shortDesc: string, labelIds: string[]): Observable<{ matchedCount: number; modifiedCount: number; resolvedLabelIds: string[] }> {
    return this.server.patchWithToken<{ matchedCount: number; modifiedCount: number; resolvedLabelIds: string[] }>(
      `${this.base}/codes`,
      { shortDesc, labelIds },
    );
  }

  // Move a label one slot up/down; server renumbers siblings. No-op at edges.
  moveLabel(id: string, direction: 'up' | 'down'): Observable<CensusActionLabel> {
    return this.server.patchWithToken<CensusActionLabel>(
      `${this.base}/${id}/move`,
      { direction },
    );
  }

  loadAll(): Observable<{
    labels: CensusActionLabel[];
    codes: CensusCodesByActionType;
  }> {
    return forkJoin({
      labels: this.listLabels(),
      codes: this.listCodes(),
    });
  }
}
